import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { IconScale } from "@tabler/icons-react";

const statusLabels = {
  'AJUIZAR': 'AJUIZAR',
  'NAO_POSSUI_ABUSIVIDADE': 'NÃO POSSUI ABUSIVIDADE',
  'FALTAM_DOCUMENTOS': 'FALTAM DOCUMENTOS',
  'CANCELADO': 'CANCELADO',
  'PROTOCOLO_URGENTE': 'PROTOCOLO URGENTE',
  'PROTOCOLADO': 'PROTOCOLADO',
  'TRANSITO_EM_JULGADO': 'TRÂNSITO EM JULGADO',
  'REAJUIZAR': 'REAJUIZAR',
  'PASTA_FEITA': 'PASTA FEITA',
  'DESATUALIZADO': 'DESATUALIZADO',
};

const getStatusLabel = (value) => {
  return statusLabels[value] || value;
};

const ContractFileModal = ({ open, onClose, contract, onSave }) => {
  const [formData, setFormData] = useState({
    status: "",
    service_obs: "",
    process_protocol_obs: "",
    complaint_protocol_number: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (contract) {
      setFormData({
        status: "AJUIZAR", // Sempre define como AJUIZAR
        service_obs: contract.service_obs || "",
        process_protocol_obs: contract.process_protocol_obs || "",
        complaint_protocol_number: contract.complaint_protocol_number || "",
      });
    }
  }, [contract]);

  const handleSave = async () => {
    if (!contract) return;

    setLoading(true);
    try {
      const response = await fetch(`/backend/contracts/${contract.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: formData.status,
          service_obs: formData.service_obs,
          process_protocol_obs: formData.process_protocol_obs,
          complaint_protocol_number: formData.complaint_protocol_number,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        alert("Contrato atualizado com sucesso!");
        if (onSave) onSave(data);
        onClose();
      } else {
        let errorMessage = "Não foi possível atualizar o contrato.";
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          console.warn("Não foi possível extrair mensagem de erro:", e);
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Erro ao atualizar contrato:", error);
      alert("Erro ao atualizar contrato. Verifique sua conexão e tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (!contract) return null;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconScale stroke={1.5} size="1.5rem" />
          <span>Ajuizar Contrato</span>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Informações do contrato */}
        <Box sx={{ 
          backgroundColor: '#f5f5f5', 
          borderRadius: 1, 
          p: 2, 
          mb: 3,
          mt: 1 
        }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Informações do Contrato
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Contrato:</strong> {contract.number || "Sem número"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Cliente:</strong> {contract.customer?.name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Banco:</strong> {contract.bank?.name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  <strong>Status Atual:</strong>
                </Typography>
                <Chip 
                  label={getStatusLabel(contract.status)} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Formulário */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nova Situação"
              value="AJUIZAR"
              fullWidth
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nº protocolo de reclamação"
              value={formData.complaint_protocol_number}
              onChange={(e) => setFormData({ ...formData, complaint_protocol_number: e.target.value })}
              fullWidth
              inputProps={{ maxLength: 30 }}
              helperText={`${formData.complaint_protocol_number.length}/30 caracteres`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observação do atendimento"
              value={formData.service_obs}
              onChange={(e) => setFormData({ ...formData, service_obs: e.target.value })}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 100 }}
              helperText={`${formData.service_obs.length}/100 caracteres`}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Observação do protocolo do processo"
              value={formData.process_protocol_obs}
              onChange={(e) => setFormData({ ...formData, process_protocol_obs: e.target.value })}
              fullWidth
              multiline
              rows={2}
              inputProps={{ maxLength: 100 }}
              helperText={`${formData.process_protocol_obs.length}/100 caracteres`}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? "Salvando..." : "Ajuizar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractFileModal;

